import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../api/client";
import PortalSidebar from "../../components/portal/PortalSidebar";
import { 
  Building2, User, Mail, Phone, MapPin, Shield,
  AlertCircle, CheckCircle, Save
} from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTenantInfo = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/portal/settings/tenant");
      setTenantInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch tenant info:", err);
      setError("Failed to load organization settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenantInfo();
  }, [fetchTenantInfo]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <PortalSidebar />

      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="settings-title">Settings</h1>
            <p className="text-slate-500 mt-1">
              Manage your account and organization settings
            </p>
          </div>

          {error && (
            <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Organization Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="org-settings-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Building2 size={24} className="text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">Organization</h2>
                      <p className="text-sm text-slate-500">Your organization details</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">Organization Name</label>
                      <p className="text-slate-900 font-medium" data-testid="org-name">{tenantInfo?.name || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">Organization ID</label>
                      <p className="text-slate-600 font-mono text-sm" data-testid="org-id">{tenantInfo?.slug || '—'}</p>
                    </div>
                    {tenantInfo?.domain && (
                      <div>
                        <label className="block text-sm font-medium text-slate-500 mb-1">Domain</label>
                        <p className="text-slate-600" data-testid="org-domain">{tenantInfo.domain}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="account-settings-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <User size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">Your Account</h2>
                      <p className="text-sm text-slate-500">Your personal account information</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">Email</label>
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-slate-400" />
                        <p className="text-slate-900" data-testid="user-email">{user?.email || '—'}</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">Role</label>
                      <span 
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"
                        data-testid="user-role"
                      >
                        <Shield size={12} />
                        {user?.role || 'User'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Card */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="security-settings-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Shield size={24} className="text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">Security</h2>
                      <p className="text-sm text-slate-500">Manage your security settings</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Password</p>
                      <p className="text-sm text-slate-500">Last changed: Unknown</p>
                    </div>
                    <button
                      className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
                      data-testid="change-password-btn"
                    >
                      Change Password
                    </button>
                  </div>
                </div>
              </div>

              {/* Notifications Preferences */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="notifications-settings-card">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
                  <p className="text-sm text-slate-500 mt-1">Configure how you receive updates</p>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Email Notifications</p>
                      <p className="text-sm text-slate-500">Receive updates via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Status Updates</p>
                      <p className="text-sm text-slate-500">Get notified when patient status changes</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
